import { EditTodoIcon } from '@/assets/icons/info-icons/editTodo';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';
import ProgressBar from './progressBar';
import { IInvestorDetailsProgressBar } from './types';

interface NotificationWithProgressBarProps {
  notificationTitle: string;
  notificationDescription: React.ReactNode;
  progressBarData: IInvestorDetailsProgressBar;
  customEditButton?: React.ReactNode;
  onClickEditButton?: () => void;
}

const NotificationWithProgressBar = ({
  notificationTitle,
  notificationDescription,
  progressBarData,
  customEditButton,
  onClickEditButton
}: NotificationWithProgressBarProps) => {
  const { isMobile } = useResponsive();

  return (
    <NotificationWrapper>
      <InfoIconWrapper>
        <InfoIcon width={26} height={26} fill={themeVar('header.base')} />
      </InfoIconWrapper>
      <NotificationBodyContainer isMobile={isMobile}>
        <NotificationBody isMobile={isMobile}>
          <NotificationTitle>{notificationTitle}</NotificationTitle>
          <NotificationDescription>{notificationDescription}</NotificationDescription>
        </NotificationBody>
        {!!progressBarData && (
          <ProgressBarWrapper isMobile={isMobile}>
            <ProgressBar progressBarData={progressBarData} />
          </ProgressBarWrapper>
        )}
        <MarginLeftAuto isMobile={isMobile}>
          {!!customEditButton ? (
            customEditButton
          ) : (
            <EditButton id="notification-with-progress-bar-edit-button" onClick={onClickEditButton} isMobile={isMobile}>
              <EditTodoIcon />
            </EditButton>
          )}
        </MarginLeftAuto>
      </NotificationBodyContainer>
    </NotificationWrapper>
  );
};

export default NotificationWithProgressBar;

const NotificationWrapper = styled.div`
  background: ${themeVar('layer.base')};
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 10px 20px;
  border-radius: 10px;

  &:hover #notification-with-progress-bar-edit-button {
    opacity: 1;
  }
`;

const InfoIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NotificationBodyContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 10px 4px;
  width: 100%;
  padding-left: 20px;
  border-left: 1px solid ${themeVar('border.base')};
`;

const NotificationBody = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '45%')};
`;

const MarginLeftAuto = styled.div<{ isMobile: boolean }>`
  margin-left: ${({ isMobile }) => (isMobile ? '0' : 'auto')};
  width: ${({ isMobile }) => (isMobile ? '100%' : 'auto')};
`;

const ProgressBarWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '50%')};
`;

const NotificationTitle = styled.p`
  font-weight: 700;
  font-size: 16px;
  color: ${themeVar('font.base')};
`;

const NotificationDescription = styled.div`
  font-weight: 400;
  font-size: 13px;
  color: ${themeVar('font.base')};
`;

const EditButton = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  height: 40px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '40px')};
  background-color: ${themeVar('action.default')};
  transition: 0.1s;
  opacity: 0;
`;
